import { Component, computed, effect, inject, input, NgZone, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { DashboardWorkersWorkedHoursObject } from '@libs/types/src/services/object-type/dashboard/dashboard-workers-worked-hours.object';
import { UserGridItemObject } from '@it2go/types';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { UIChart } from '@openng/optimus-ui/chart';
import { Chart, ChartData, ChartOptions, TooltipModel } from 'chart.js';
import { DashboardChartWidgetStore } from '../dashboard-chart-widget.store';
import {
    dashboardChartTooltipPosition,
    dashboardServiceIds,
    dashboardShiftMinutes,
} from '../dashboard-chart-widget.utils';
import {
    dashboardWorkersWorkedHoursFragment,
    dashboardWorkerResourceFragment,
} from '../dashboard-chart-widget.query';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { CalendarWorkingHoursResolver } from '../../../../../shared/service/calendar/calendar-working-hours.resolver';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { Operator } from '../../../../../../../../../data-grid/src/grid/grid-request.dto';
import { formatDuration } from '../../../../../shared/pipes/format-duration.pipe';
import { WorkersWorkedHoursChartItem } from './workers-worked-hours-widget.types';
import { DashboardChartTooltipComponent } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.component';
import { DashboardChartTooltipData } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.types';
import { TranslateService } from '@ngx-translate/core';
import {
    DateScrollerInputComponent,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.component';
import {
    DateScrollerRange,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.types';

@Component({
    selector: 'app-workers-worked-hours-widget',
    templateUrl: './workers-worked-hours-widget.component.html',
    styleUrl: './workers-worked-hours-widget.component.scss',
    providers: [DashboardChartWidgetStore],
    imports: [
        FormField,
        DateScrollerInputComponent,
        DashboardChartTooltipComponent,
        UIChart,
        LoadingComponent,
        EmptyStateComponent,
    ],
})
export class WorkersWorkedHoursWidgetComponent {
    protected readonly store = inject(DashboardChartWidgetStore);
    private readonly workingHoursResolver = inject(CalendarWorkingHoursResolver);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);
    private readonly chartColor = '#4b82db';

    public readonly items = input<{ serviceId: string; widget: UserWidgetSetting }[]>([]);

    protected readonly dateFormData = signal({ date: new Date() as Date | null });
    protected readonly dateForm = form(this.dateFormData);
    protected readonly period = signal<DateScrollerRange>(this.currentDay());
    protected readonly statisticsLoading = this.store.loadingFor('getDashboardWorkersWorkedHours');
    protected readonly resourcesLoading = this.store.loadingFor('getUsers');
    protected readonly loading = computed(() => this.statisticsLoading() || this.resourcesLoading());
    protected readonly rows = computed<DashboardWorkersWorkedHoursObject[]>(
        () => this.store.data.getDashboardWorkersWorkedHours?.() ?? [],
    );
    protected readonly resources = computed<UserGridItemObject[]>(
        () => this.store.data.getUsers?.()?.items ?? [],
    );
    protected readonly chartItems = computed<WorkersWorkedHoursChartItem[]>(() => {
        const date = this.period().dateFrom;
        const rowsByUser = new Map<string, DashboardWorkersWorkedHoursObject[]>();
        for (const row of this.rows()) {
            rowsByUser.set(row.userId, [...(rowsByUser.get(row.userId) ?? []), row]);
        }

        return [...rowsByUser.entries()].map(([userId, rows]) => {
            const workedMinutes = rows.reduce((total, row) => total + row.workedMinutes, 0);
            const scheduledMinutes = this.getScheduledMinutes(userId, date);
            return {
                userId,
                worker: rows[0].worker,
                workedMinutes,
                scheduledMinutes,
                percentage: scheduledMinutes > 0 ? workedMinutes / scheduledMinutes * 100 : 0,
            };
        }).filter(item => item.scheduledMinutes > 0 || item.workedMinutes > 0);
    });
    protected readonly chartData = computed<ChartData<'bar'>>(() => ({
        labels: this.chartItems().map(item => item.worker),
        datasets: [{
            label: '',
            data: this.chartItems().map(item => item.workedMinutes / 60),
            backgroundColor: this.chartColor,
            borderColor: '#396cb9',
            borderWidth: 1,
        }],
    }));
    protected readonly chartTooltip = signal<DashboardChartTooltipData | null>(null);
    protected readonly chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
                external: ({ chart, tooltip }) => this.updateChartTooltip(chart, tooltip),
            },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    constructor() {
        effect(() => {
            const serviceIds = dashboardServiceIds(this.items());
            const period = this.period();
            if (!serviceIds.length) return;

            this.store.api.getDashboardWorkersWorkedHours({
                serviceIds,
                date: period.dateFrom,
            }, dashboardWorkersWorkedHoursFragment);
        });

        effect(() => {
            const userIds = [...new Set(this.rows().map(row => row.userId))];
            if (!userIds.length) return;
            const filter = new FilterBuilder()
                .where('id', userIds, Operator.IN)
                .limit(1_000);

            this.store.api.getUsers(filter.filter(), dashboardWorkerResourceFragment);
        });
    }

    private updateChartTooltip(chart: Chart, tooltip: TooltipModel<'bar'>): void {
        if (!tooltip.opacity) {
            this.hideChartTooltip();
            return;
        }

        const dataIndex = tooltip.dataPoints[0]?.dataIndex;
        const worker = dataIndex === undefined ? undefined : this.chartItems()[dataIndex];
        if (!worker || worker.workedMinutes <= 0) {
            this.hideChartTooltip();
            return;
        }

        this.zone.run(() => this.chartTooltip.set({
            ...dashboardChartTooltipPosition(chart.width, chart.height, tooltip.caretX, tooltip.caretY),
            title: worker.worker,
            summary: [
                {
                    label: this.translate.instant('dashboard.workersWorkedHours.worked'),
                    value: formatDuration(Math.round(worker.workedMinutes)),
                    color: this.chartColor,
                },
                {
                    label: this.translate.instant('dashboard.workersWorkedHours.shift'),
                    value: `${Math.round(worker.percentage)} %`,
                },
            ],
        }));
    }

    private hideChartTooltip(): void {
        this.zone.run(() => this.chartTooltip.set(null));
    }

    private getScheduledMinutes(userId: string, date: Date): number {
        const resource = this.resources().find(item => item.id === userId);
        if (!resource) return 0;

        const workingHours = this.workingHoursResolver.resolve({
            date,
            resources: [resource],
            services: this.store.globalGlobalStore.organizationStore.servicesInContext(),
            selectedServiceIds: dashboardServiceIds(this.items()),
        })[0];

        return dashboardShiftMinutes(workingHours?.intervals ?? []);
    }

    private currentDay(): DateScrollerRange {
        const dateFrom = new Date();
        dateFrom.setHours(0, 0, 0, 0);
        const dateTo = new Date(dateFrom);
        dateTo.setHours(23, 59, 59, 999);
        return { dateFrom, dateTo, type: TimePeriodEnum.Day, length: 1 };
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
}
